import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getApiBaseUrl, readJsonResponse } from '../apiBase';
import {
    AS_REPAIR_LIST_EXPORT_MODE_LABELS,
    buildDefaultAsRepairListExportColumns,
    normalizeAsRepairListExportConfig,
    resolveAsRepairListExportColumns,
} from '../utils/asRepairListExportConfig';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const INPUT_TYPE_LABELS = {
    quantity: '수량',
    auto: '자동 계산',
    date: '날짜',
    textarea: '장문',
    dropdown: '드롭다운',
    status: '상태',
};

const fieldOptionLabel = (field) => {
    const typeLabel = INPUT_TYPE_LABELS[field.inputType];
    return typeLabel ? `${field.label} (${typeLabel})` : field.label;
};

const moveItem = (list, index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= list.length) {
        return list;
    }
    const next = [...list];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    return next;
};

const AsRepairListExportSettingsModal = ({
    open,
    mode,
    fields,
    savedConfig,
    currentUserName,
    onClose,
    onSaved,
    showAppToast,
}) => {
    const otherMode = mode === 'external' ? 'internal' : 'external';
    const modeLabel = AS_REPAIR_LIST_EXPORT_MODE_LABELS[mode] || '';
    const otherModeLabel = AS_REPAIR_LIST_EXPORT_MODE_LABELS[otherMode] || '';
    const normalizedSaved = useMemo(() => normalizeAsRepairListExportConfig(savedConfig), [savedConfig]);
    const [columns, setColumns] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setColumns(normalizedSaved[mode] || []);
        }
    }, [open, mode, normalizedSaved]);

    const resolved = useMemo(
        () => resolveAsRepairListExportColumns(fields, columns),
        [fields, columns],
    );
    const usedFieldKeys = useMemo(
        () => new Set(resolved.map((col) => col.field?.key).filter(Boolean)),
        [resolved],
    );
    const unmatchedCount = resolved.filter((col) => !col.field).length;
    const isDirty = JSON.stringify(columns) !== JSON.stringify(normalizedSaved[mode] || []);

    if (!open) {
        return null;
    }

    const updateColumn = (index, patch) => {
        setColumns((prev) => prev.map((col, i) => (i === index ? { ...col, ...patch } : col)));
    };

    const addFieldColumn = (fieldKey) => {
        const field = fields.find((f) => f.key === fieldKey);
        if (!field) {
            return;
        }
        setColumns((prev) => [...prev, { header: field.label, fieldKey: field.key }]);
    };

    const handleClose = () => {
        if (isDirty && !window.confirm('저장하지 않은 변경 사항이 있습니다. 닫을까요?')) {
            return;
        }
        onClose();
    };

    const handleSave = async () => {
        if (!currentUserName) {
            showAppToast('로그인이 필요합니다.', 'error');
            return;
        }
        const trimmed = columns.map((col) => ({ ...col, header: String(col.header || '').trim() }));
        if (trimmed.length === 0) {
            showAppToast('엑셀 열을 1개 이상 추가해주세요.', 'error');
            return;
        }
        if (trimmed.some((col) => !col.header)) {
            showAppToast('비어 있는 열 제목이 있습니다.', 'error');
            return;
        }
        setBusy(true);
        try {
            const nextConfig = { ...normalizedSaved, [mode]: trimmed };
            const res = await fetch(`${getApiBaseUrl()}/as-repair-list-export-config`, {
                method: 'PUT',
                headers: JSON_HEADERS,
                body: JSON.stringify({ requesterName: currentUserName, config: nextConfig }),
            });
            const data = await readJsonResponse(res);
            onSaved(data?.config || nextConfig);
            showAppToast(`리스트 파일 설정(${modeLabel})이 저장되었습니다.`, 'success');
            onClose();
        } catch (error) {
            showAppToast(error?.message || '저장에 실패했습니다.', 'error');
        } finally {
            setBusy(false);
        }
    };

    return createPortal(
        <div className="modal-backdrop" onClick={handleClose} role="presentation">
            <div
                className="modal-content list-export-settings-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="list-export-settings-title"
            >
                <div className="modal-header">
                    <h3 id="list-export-settings-title">리스트 파일 설정({modeLabel})</h3>
                    <button type="button" className="modal-close" onClick={handleClose} disabled={busy} aria-label="닫기">×</button>
                </div>
                <p className="list-export-settings-help">
                    A/S 개인 관리 「수리완료 리스트 다운로드({modeLabel})」 엑셀 파일의 열을 순서대로 설정합니다.
                    1행은 열 제목, 2행부터 선택한 A/S 목록이 들어갑니다. 연결 필드를 「자동」으로 두면 열 제목과 같은 이름의 필드를 찾아 넣습니다.
                </p>

                <div className="list-export-settings-toolbar">
                    <select
                        className="field-input list-export-settings-add-select"
                        value=""
                        onChange={(e) => addFieldColumn(e.target.value)}
                        disabled={busy}
                    >
                        <option value="">+ 필드로 열 추가…</option>
                        {fields.map((f) => (
                            <option key={f.key} value={f.key}>
                                {usedFieldKeys.has(f.key) ? '✓ ' : ''}{fieldOptionLabel(f)}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="button button-cancel"
                        onClick={() => setColumns((prev) => [...prev, { header: '', fieldKey: '' }])}
                        disabled={busy}
                    >
                        빈 열 추가
                    </button>
                    <button
                        type="button"
                        className="button button-cancel"
                        onClick={() => {
                            if (window.confirm(`${otherModeLabel} 설정의 열 목록으로 바꿀까요?`)) {
                                setColumns(normalizedSaved[otherMode] || []);
                            }
                        }}
                        disabled={busy}
                    >
                        {otherModeLabel} 설정 불러오기
                    </button>
                    <button
                        type="button"
                        className="button button-cancel"
                        onClick={() => {
                            if (window.confirm(`${modeLabel} 열 목록을 기본값으로 되돌릴까요?`)) {
                                setColumns(buildDefaultAsRepairListExportColumns(mode));
                            }
                        }}
                        disabled={busy}
                    >
                        기본값 복원
                    </button>
                </div>

                <div className="list-export-settings-table-wrap">
                    <table className="product-reg-settings-table list-export-settings-table">
                        <thead>
                            <tr>
                                <th>열</th>
                                <th>엑셀 열 제목</th>
                                <th>연결 필드</th>
                                <th>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {columns.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="qty-chart-empty">열이 없습니다. 위에서 필드를 선택해 추가하세요.</td>
                                </tr>
                            ) : null}
                            {columns.map((col, index) => {
                                const match = resolved[index];
                                const missing = !match?.field;
                                return (
                                    <tr key={index} className={missing ? 'list-export-settings-row--missing' : ''}>
                                        <td className="product-reg-settings-order">{index + 1}</td>
                                        <td>
                                            <input
                                                type="text"
                                                className="field-input"
                                                value={col.header}
                                                onChange={(e) => updateColumn(index, { header: e.target.value })}
                                                placeholder="엑셀 1행에 들어갈 제목"
                                                disabled={busy}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                className="field-input"
                                                value={col.fieldKey}
                                                onChange={(e) => {
                                                    const fieldKey = e.target.value;
                                                    const field = fields.find((f) => f.key === fieldKey);
                                                    updateColumn(index, {
                                                        fieldKey,
                                                        ...(field && !String(col.header || '').trim() ? { header: field.label } : {}),
                                                    });
                                                }}
                                                disabled={busy}
                                            >
                                                <option value="">
                                                    {!col.fieldKey && match?.field
                                                        ? `자동 → ${match.field.label}`
                                                        : '자동 (제목과 같은 필드)'}
                                                </option>
                                                {fields.map((f) => (
                                                    <option key={f.key} value={f.key}>{fieldOptionLabel(f)}</option>
                                                ))}
                                                {col.fieldKey && !fields.some((f) => f.key === col.fieldKey) ? (
                                                    <option value={col.fieldKey}>{col.fieldKey} (삭제된 필드)</option>
                                                ) : null}
                                            </select>
                                            {missing ? (
                                                <p className="qty-chart-auto-hint qty-chart-auto-hint--empty">
                                                    연결된 필드 없음 — 빈 칸으로 출력됩니다.
                                                </p>
                                            ) : null}
                                        </td>
                                        <td className="product-reg-settings-actions">
                                            <button type="button" className="button button-cancel model-edit-button" onClick={() => setColumns((prev) => moveItem(prev, index, -1))} disabled={busy || index === 0}>▲</button>
                                            <button type="button" className="button button-cancel model-edit-button" onClick={() => setColumns((prev) => moveItem(prev, index, 1))} disabled={busy || index === columns.length - 1}>▼</button>
                                            <button type="button" className="button button-cancel model-edit-button" onClick={() => setColumns((prev) => prev.filter((_, i) => i !== index))} disabled={busy}>삭제</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="list-export-settings-footer">
                    <span className="list-export-settings-summary">
                        총 {columns.length}열
                        {unmatchedCount > 0 ? ` · 연결 안 된 열 ${unmatchedCount}개` : ''}
                        {isDirty ? ' · 저장되지 않은 변경 사항' : ''}
                    </span>
                    <button type="button" className="button button-cancel" onClick={handleClose} disabled={busy}>취소</button>
                    <button type="button" className="button" onClick={handleSave} disabled={busy || !isDirty}>
                        {busy ? '저장 중...' : '저장'}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AsRepairListExportSettingsModal;
